/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'

import colors from '@sportninja/common/constants/appColors'

import { font, weightSemiBold } from 'src/components/css'
import { gameViewsLabel } from '../utils/i18n'

export interface GridStatusBarProps {
  /** Rows the grid holds — everything paged in so far. */
  loadedCount: number
  /** Rows the grid is actually showing, after filters and search. */
  displayedCount: number
  selectedCount: number
  isFiltered: boolean
  /** True while the API still has pages we have not pulled. */
  hasMorePages: boolean
}

/**
 * The reference grid's status bar, rebuilt on Community.
 *
 * AG Grid's own `statusBar` (`agTotalAndFilteredRowCountComponent`,
 * `agSelectedRowCountComponent`) is Enterprise, and the two numbers it shows are
 * numbers we already have — so this is the same information in the same place,
 * for no licence.
 *
 * The one addition is deliberate. Filtering here is CLIENT-side over the pages
 * loaded so far, not a server query, so "3 of 40" can sit above a season with
 * four hundred games in it. Saying which population the filter ran against is
 * the difference between a count and a misleading count.
 */
const GridStatusBar = ({
  loadedCount,
  displayedCount,
  selectedCount,
  isFiltered,
  hasMorePages,
}: GridStatusBarProps) => {
  'use memo'

  return (
    <div css={barStyles} data-testid='game-details-status-bar'>
      <div css={groupStyles}>
        <span css={countStyles}>
          {isFiltered
            ? gameViewsLabel('gameGridStatusFiltered', {
                shown: displayedCount,
                loaded: loadedCount,
              })
            : gameViewsLabel('gameGridStatusLoaded', { count: loadedCount })}
        </span>
        {selectedCount > 0 && (
          <span css={selectedStyles} data-testid='game-details-selected-count'>
            {gameViewsLabel('gameGridStatusSelected', { count: selectedCount })}
          </span>
        )}
      </div>

      {isFiltered && hasMorePages && (
        <span css={scopeStyles}>
          {gameViewsLabel('gameGridStatusFilterScope')}
        </span>
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const barStyles = css`
  ${font.body}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 2px 0;
  font-size: 13px;
  color: ${colors.NEUTRAL_50};
`

const groupStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
`

const countStyles = css`
  font-variant-numeric: tabular-nums;
`

const selectedStyles = css`
  ${weightSemiBold}
  font-variant-numeric: tabular-nums;
  color: ${colors.NEUTRAL_0};
`

const scopeStyles = css`
  color: ${colors.NEUTRAL_100};
`

export default GridStatusBar
